/* eslint-disable react/static-property-placement */
import React, { Component, createRef, RefObject } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { toTagSetsArray } from "@formant/core/common/toTagSetsArray";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IFilter } from "@formant/core/model/IFilter";
import { ITags } from "@formant/core/model/ITags";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { IView } from "@formant/core/model/IView";
import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";
import { Icon } from "@formant/ui/component/common/Icon";

import { ViewContext } from "../../context/TeleopContext/ViewContext";
import defaultLogo from "../../static/images/logo/formant-default-logo.svg";
import {
    authStore,
    deviceStore,
    groupStore,
    observeStore,
    onlineDevicesStore,
    routerStore,
    viewStore,
} from "../../stores";
import { hashObservable } from "../../utils/hashObservable";
import { Avatar } from "../common/Avatar";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterToggle } from "../common/FilterToggle";
import { Header } from "../common/Header";
import { DeviceStatus } from "../DeviceStatus";
import { EditViewHeader } from "../EditViewHeader";
import { emptyEventListState } from "../EventList/EventListState";
import { HomeTabs } from "../HomeTabs";
import { OverviewViewName } from "../Overview/OverviewV2/OverviewV2";
import styles from "../ViewDevice/ViewDeviceHeader.scss";
import { ConnectedHeaderNotificationButton } from "../ViewDevice/ViewDeviceHeaders/HeaderNotificationButton";
import {
    DropdownContainer,
    KioskLogo,
    ProfileDropdown,
    RightButtons,
    ScrollItem,
} from "./ViewMultiDeviceHeader.styles";

// Preload DeviceDropdown only when authenticated
const DeviceDropdownPromise = () => {
    return Promise.all([
        import("./DeviceDropdown"),
        authStore.authenticated
            ? onlineDevicesStore.fetch()
            : Promise.resolve(),
    ]).then(([module]) => ({
        default: module.DeviceDropdown,
    }));
};
const DeviceDropdownComponent = React.lazy(DeviceDropdownPromise);
// Trigger preload only if authenticated
if (authStore.authenticated) {
    void DeviceDropdownPromise();
}

export interface IViewMultiDeviceHeaderProps {
    view?: IView;
    deviceIds: string[];
    viewId?: Uuid;
    groupId: Uuid;
    filter: IFilter;
    context: IFilter;
    children?: React.ReactNode;
    layoutType?: LayoutType;
    onChangeViewId: (id: Uuid) => void;
    onAnnotate?: () => void;
    onChangeFilter: (filter: IFilter) => void;
    onSaveLayout?: (layout: ITeleopLayout) => void;
}

interface IViewMultiDeviceHeaderState {
    isOpen: boolean;
    isAccountMenuOpen: boolean;
    clicked: boolean;
    eventFilter: IEventFilter;
    previousTags?: ITags;
}

type EventHandler = (event: MouseEvent | TouchEvent) => void;

@observer
export class ViewMultiDeviceHeader extends Component<
    IViewMultiDeviceHeaderProps,
    IViewMultiDeviceHeaderState
> {
    @hashObservable()
    private filterVisible: boolean = false;

    @observable
    private searchValue: string;

    @observable
    private views: IView[] = [];

    private onlineStatusInterval: NodeJS.Timeout | null = null;

    static contextType = ViewContext;
    declare context: React.ContextType<typeof ViewContext>;

    private ref: RefObject<HTMLDivElement>;
    private removeClickListener?: () => void;

    constructor(props: IViewMultiDeviceHeaderProps) {
        super(props);
        this.state = {
            isOpen: false,
            isAccountMenuOpen: false,
            clicked: false,
            eventFilter: emptyEventListState.filter,
        };
        this.ref = createRef<HTMLDivElement>();
        this.toggleDropdown = this.toggleDropdown.bind(this);
        this.searchValue = "";
        this.updateViewList();
    }

    componentDidUpdate(prevProps: Readonly<IViewMultiDeviceHeaderProps>): void {
        if (
            prevProps.groupId !== this.props.groupId ||
            (prevProps.view &&
                this.props.view &&
                prevProps.view.name !== this.props.view.name)
        ) {
            this.updateViewList();
        }
    }

    updateViewList() {
        fork(
            (async () => {
                const group = await groupStore.fetchGet(this.props.groupId);
                if (group) {
                    const views = await viewStore.fetchListSmartFleet(group);
                    this.views = views.filter(
                        val => val.name !== OverviewViewName,
                    );
                }
            })(),
        );
    }

    toggleDropdown() {
        if (!this.state.clicked && !this.state.isAccountMenuOpen) {
            this.setState(prevState => ({
                isOpen: !prevState.isOpen,
                clicked: true,
            }));
            setTimeout(() => {
                this.setState({ clicked: false });
            }, 100);
        }
    }

    toggleAccountDropdown() {
        if (!this.state.clicked && !this.state.isOpen) {
            this.setState(prevState => ({
                isAccountMenuOpen: !prevState.isAccountMenuOpen,
                clicked: true,
            }));
            setTimeout(() => {
                this.setState({ clicked: false });
            }, 100);
        }
    }

    private checkOnClickOutside(
        ref: RefObject<HTMLDivElement>,
        handler: EventHandler,
    ): void {
        const listener = (event: MouseEvent | TouchEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return;
            }

            // Check if mute modal exists and is open
            const muteModal = document.querySelector('[role="dialog"]');
            if (muteModal) {
                return;
            }

            handler(event);
        };
        document.addEventListener("mousedown", listener);
        document.addEventListener("touchstart", listener);
        this.removeClickListener = () => {
            document.removeEventListener("mousedown", listener);
            document.removeEventListener("touchstart", listener);
        };
    }

    componentDidMount() {
        this.checkOnClickOutside(this.ref, () => {
            this.toggleDropdown();
            this.toggleAccountDropdown();
        });

        this.onlineStatusInterval = setInterval(() => {
            void onlineDevicesStore.fetch();
        }, 10000);
    }

    componentWillUnmount() {
        if (this.removeClickListener) {
            this.removeClickListener();
        }

        if (this.onlineStatusInterval) {
            clearInterval(this.onlineStatusInterval);
            this.onlineStatusInterval = null;
        }
    }

    public render() {
        const { props, searchValue } = this;
        const {
            view,
            viewId,
            layoutType,
            filter,
            onChangeViewId,
            deviceIds,
            groupId,
        } = props;
        const isKiosk = authStore.hasAccess("kiosk");
        const { navigate, routeTo } = routerStore;

        // if (!view || !viewId) {
        //     return false;
        // }

        const getTitle = (): string | undefined => {
            if (groupId) {
                return (groupStore.get(groupId) || undefined)?.name;
            }
        };

        const passesFilter = (name: string): boolean =>
            searchValue === "" ||
            name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase());

        if (!deviceIds || !groupId) {
            return false;
        }

        const group = groupStore.get(groupId);
        const allGroups = groupStore.list() || [];

        if (!group) {
            return false;
        }

        const devices =
            deviceIds
                .map(_ => deviceStore.get(_) || undefined)
                .filter(_ => !!_?.id && !!_?.name && passesFilter(_.name))
                .map(_ => defined(_)) || [];

        // Calculate online devices count
        const online = onlineDevicesStore.online();
        const onlineCount = online
            ? deviceIds.filter(id => online.has(id)).length
            : 0;

        const dropdown =
            devices.length > 0 ? (
                <React.Suspense fallback={null}>
                    <DeviceDropdownComponent
                        isOpen={this.state.isOpen}
                        searchValue={searchValue}
                        devices={devices}
                        allGroups={allGroups}
                        dropdownRef={this.ref}
                        onChangeSearchValue={this.onChangeSearchValue}
                    />
                </React.Suspense>
            ) : null;

        const accountDropDown = this.state.isAccountMenuOpen && (
            <ProfileDropdown ref={this.ref}>
                <ScrollItem
                    onClick={async () => {
                        await navigate(routeTo.profile());
                    }}
                >
                    <li>
                        <div>
                            <Icon name="settings" size="18" />
                            <span>Profile</span>
                        </div>
                        <span />
                    </li>
                </ScrollItem>
                <ScrollItem
                    onClick={async () => {
                        await authStore.logout();
                    }}
                >
                    <li>
                        <div>
                            <Icon name="arrow-left" size="18" />
                            <span>Logout</span>
                        </div>
                        <span />
                    </li>
                </ScrollItem>
            </ProfileDropdown>
        );

        const filterToggle = (
            <RightButtons>
                {isKiosk ? (
                    <div
                        style={{
                            cursor: "pointer",
                        }}
                        onClick={async () => {
                            this.toggleAccountDropdown();
                        }}
                    >
                        <Avatar userId={authStore.userId} size="medium" />
                    </div>
                ) : (
                    <FilterToggle
                        onClicked={this.onToggleFilter}
                        active={toTagSetsArray(filter?.tags || []).length > 0}
                    />
                )}
            </RightButtons>
        );

        if (observeStore.isCustomizing && viewId) {
            return (
                <Header
                    title={getTitle() || ""}
                    className={classnames(
                        styles["view-device-header"],
                        "h-[6.25rem]",
                        styles.editing,
                    )}
                    hideMenu={isKiosk}
                    subTitle={`${onlineCount}/${deviceIds.length} devices online`}
                    onHeaderClick={() => {
                        this.onEditDevice();
                        this.toggleDropdown();
                    }}
                >
                    {isKiosk && (
                        <div
                            style={{
                                display: "inline-block",
                            }}
                        >
                            hey
                        </div>
                    )}
                    <EditViewHeader view={view} deviceIds={deviceIds}>
                        {dropdown}
                        {accountDropDown}
                        <DeviceStatus online={true} />
                    </EditViewHeader>
                </Header>
            );
        }

        const views = this.views;

        return (
            <DropdownContainer>
                <Header
                    title={getTitle() || ""}
                    className={classnames(
                        styles["view-device-header"],
                        this.filterVisible && styles["filter-visible"],
                        observeStore.fullscreen && styles.fullscreen,
                    )}
                    hideMenu={isKiosk}
                    subTitle={`${onlineCount}/${deviceIds.length} devices online`}
                    onHeaderClick={this.toggleDropdown}
                    showDropdownIcon={devices.length > 0}
                    preIcon={
                        isKiosk && (
                            <KioskLogo
                                className="formant-branding-logo"
                                src={defaultLogo}
                            />
                        )
                    }
                >
                    {dropdown}
                    {accountDropDown}
                    {!isKiosk && <DeviceStatus id={undefined} online={true} />}
                    <HomeTabs
                        device={null}
                        value={viewId}
                        context={this.props.context}
                        filterActive={this.filterVisible}
                        filter={filter}
                        layoutType={layoutType || "observe"}
                        onEditDevice={this.onEditDevice}
                        onChangeViewId={onChangeViewId}
                        views={views}
                        eventFilter={this.state.eventFilter}
                        group={group}
                    />
                    <ConnectedHeaderNotificationButton
                        context={this.props.context}
                        eventFilter={this.state.eventFilter}
                        onClick={this.showEvents}
                        selected={false}
                    />
                    {filterToggle}
                </Header>
                <FilterDrawer
                    visible={this.filterVisible}
                    filter={filter}
                    hideDevices={false}
                    context={this.props.context}
                    onChangeFilter={this.props.onChangeFilter}
                />
            </DropdownContainer>
        );
    }

    private showEvents = () => {
        const { navigate, routeTo } = routerStore;
        void navigate(
            routeTo.smartFleetEventsWithView({
                id: this.props.groupId,
            }),
        );
    };

    private onEditDevice = () => {
        observeStore.editingDevice = true;
    };

    private onChangeSearchValue = (value: string) => {
        this.searchValue = value;
    };

    private onToggleFilter = () => {
        this.filterVisible = !this.filterVisible;
    };
}
